import { Alert, Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  type CapDossierCover as Cover,
  type CapDossierFinding,
  type CapDossierSeverity,
} from '@dosfilos/domain';

// ─── La portada del expediente ──────────────────────────────────────────────
//
// Responde lo primero que pregunta quien abre una ronda: «¿qué tengo delante y
// qué debería mirar?». Debajo sigue el expediente completo, en el orden que
// enseña el método — auditoría, evidencia, lo que declaró el centro, decisión.
//
// No calcula nada: `buildCapDossierCover` lo hace en el dominio, con el MISMO
// paquete que se muestra abajo. Acá sólo se traduce y se ordena visualmente, que
// es lo que impide que la portada y el expediente lleguen a decir cosas
// distintas — y es lo que permitirá que el PDF imprima esto mismo.

const SEVERITY_COLOR: Record<CapDossierSeverity, 'error' | 'warning' | 'info'> = {
  blocking: 'error',
  concern: 'warning',
  note: 'info',
};

function FindingRow({ finding }: { finding: CapDossierFinding }): JSX.Element {
  const { t } = useTranslation('cap');
  // Las áreas ya tienen nombre en `cap.subjectAreas`: el mismo que la tabla de
  // la auditoría muestra abajo. Reusarlo evita dos nombres para una cosa.
  const area = finding.area ? t(`subjectAreas.${finding.area}`) : '';
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Chip
        size="small"
        color={SEVERITY_COLOR[finding.severity]}
        variant={finding.severity === 'blocking' ? 'filled' : 'outlined'}
        label={t(`dossier.severity.${finding.severity}`)}
        sx={{ minWidth: 92, mt: 0.25 }}
      />
      <Typography variant="body2">
        {t(`dossier.findings.${finding.code}`, { ...finding.params, area })}
      </Typography>
    </Stack>
  );
}

export function CapDossierCover({ cover }: { cover: Cover }): JSX.Element {
  const { t } = useTranslation('cap');
  const nothingBlocks = cover.counts.blocking === 0;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderWidth: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t('dossier.title')}
        </Typography>
        <Chip
          size="small"
          color={cover.overallStatus === 'compliant' ? 'success' : 'warning'}
          label={t(`compliance.status.${cover.overallStatus}`, {
            defaultValue: t(`dossier.status.${cover.overallStatus}`),
          })}
        />
        {cover.stacked && <Chip size="small" variant="outlined" label={t('dossier.stacked')} />}
      </Stack>

      {/* El total NO es el veredicto: cada área lleva su propio mínimo. Se
          muestra igual porque el revisor lo va a buscar, con los hallazgos al
          lado para que no se lea solo. */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t('dossier.tcu', { actual: cover.totalActualTcu, min: cover.totalMinTcu })}
        {' · '}
        {t('dossier.evidenceLine', {
          courses: cover.evidence.courses,
          sections: cover.evidence.sections,
        })}
        {cover.evidence.hasMatrix ? ` · ${t('dossier.withMatrix')}` : ''}
      </Typography>

      <Divider sx={{ my: 1.75 }} />

      {nothingBlocks && cover.findings.length === 0 ? (
        <Alert severity="success" variant="outlined">
          {t('dossier.nothingToFlag')}
        </Alert>
      ) : (
        <Stack spacing={1.25}>
          {cover.findings.map((f, i) => (
            <FindingRow key={`${f.code}-${f.area ?? i}`} finding={f} />
          ))}
        </Stack>
      )}

      {/* Informa, no decide: quien certifica es el revisor. */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {nothingBlocks ? t('dossier.readsAsMeeting') : t('dossier.readsAsShort')}
        </Typography>
      </Box>
    </Paper>
  );
}
